import { GET_SURVEY_QUESTIONS, GET_PUBLIC_SURVEY_QUESTIONS, GET_QUALTRICS_SURVEY_QUESTIONS } from '../../fetchTypes';
import {
  createDefaultRequestInitialState,
  createSelectorById,
  createSelectorByIdList,
  defaultRequestByIdReducer,
} from './withFetch';
import { getQualtricsToLimeSurveyType } from '../../../constants/qualtricsQuestionTypes';

const baseInitialState = {
  questionByQid: {},
};
const initialState = createDefaultRequestInitialState(baseInitialState);
export const actionDataToId = (data) => data.projectId;

// Qualtrics question transformation function
const transformQualtricsQuestions = (qualtricsResponse) => {
  // Handle different possible response formats
  let elements = null;
  
  if (qualtricsResponse?.data?.response?.result?.elements) {
    // Original expected format
    elements = qualtricsResponse.data.response.result.elements;
  } else if (qualtricsResponse?.result?.elements) {
    // Direct result.elements format
    elements = qualtricsResponse.result.elements;
  } else if (Array.isArray(qualtricsResponse?.elements)) {
    // Direct elements array
    elements = qualtricsResponse.elements;
  } else {
    return [];
  }

  return elements.map((question, index) => {
    // Transform basic question data
    const questionTag = question.DataExportTag || question.QuestionID.replace('QID', 'Q');
    const transformedQuestion = {
      qid: question.QuestionID,
      sid: questionTag, // Use consistent naming
      gid: '1', // Default group
      type: getQualtricsToLimeSurveyType(question),
      title: questionTag, // Use consistent naming
      question: question.QuestionText || question.QuestionDescription || '',
      preg: '',
      help: '',
      other: question.Choices && Object.values(question.Choices).some(choice => choice.TextEntry === 'true') ? 'Y' : 'N',
      mandatory: question.Validation?.Settings?.ForceResponse === 'ON' ? 'Y' : 'N',
      question_order: index + 1,
      language: 'en',
      scale_id: 0,
      parent_qid: '0',
      same_default: 0,
      same_script: '',
      relevance: '1',
      // Keep original Qualtrics data for reference
      QuestionType: question.QuestionType,
      Selector: question.Selector,
      SubSelector: question.SubSelector,
      DataExportTag: question.DataExportTag
    };

    return transformedQuestion;
  });
};

const reducer = defaultRequestByIdReducer({
  actionDataToId,
  resultMapper: (data, action) => {
    // Handle Qualtrics questions differently
    if (action && action.fetchType === GET_QUALTRICS_SURVEY_QUESTIONS) {
      const transformedQuestions = transformQualtricsQuestions(data);
      // Return both transformed questions and original data
      return {
        questions: transformedQuestions,
        originalQualtricsData: data
      };
    }
    // Default LimeSurvey format
    return (data || {}).response || [];
  },
  baseInitialState,
  extraOnSuccess: (state, projectId) => {
    const surveyQuestionsResult = state.byId[projectId].result;
    const nextState = { ...state, questionByQid: { ...state.questionByQid } };
    
    // Handle different result formats
    let surveyQuestions = [];
    if (surveyQuestionsResult && surveyQuestionsResult.questions) {
      // Qualtrics format with wrapped data
      surveyQuestions = surveyQuestionsResult.questions || [];
      // Store the original data in the state for later access
      nextState.byId = {
        ...nextState.byId,
        [projectId]: {
          ...nextState.byId[projectId],
          result: surveyQuestionsResult.questions, // Keep only questions in result
          originalQualtricsData: surveyQuestionsResult.originalQualtricsData
        }
      };
    } else {
      // LimeSurvey format
      surveyQuestions = Array.isArray(surveyQuestionsResult) ? surveyQuestionsResult : [];
    }
    
    // Ensure surveyQuestions is an array before calling forEach
    if (Array.isArray(surveyQuestions)) {
      surveyQuestions.forEach((q) => {
        if (q && q.qid) {
          nextState.questionByQid[q.qid] = q;
        }
      });
    }
    
    return nextState;
  },
});

export default (state = initialState, action) => {
  switch (action.fetchType) {
    case GET_SURVEY_QUESTIONS:
      return reducer(state, action);
    case GET_PUBLIC_SURVEY_QUESTIONS:
      return reducer(state, action);
    case GET_QUALTRICS_SURVEY_QUESTIONS:
      return reducer(state, action);
    default:
      return state;
  }
};

const selfSelector = (state) => state.fetcher.surveyQuestions;
export const selectFetcherSurveyQuestions = selfSelector;
export const selectSurveyQuestionsByProjectId =
  createSelectorById(selfSelector);
export const selectSurveyQuestionsListByProjectIdList =
  createSelectorByIdList(selfSelector);
export const selectSurveyQuestionByQid =
  (qid, stateSelector = selfSelector) =>
  (state) =>
    stateSelector(state).questionByQid[qid];
export const selectSurveyQuestionListByQidList = (qidList) => (state) =>
  qidList.map((qid) => selectSurveyQuestionByQid(qid)(state));
